import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { distinctUntilChanged, filter, map, mapTo, pairwise, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { compareStringsIgnoreCase, emitValueIfValid, getControlValueChanges, ignoreNullish } from '@aiwyn/shared';
import { MatAccordion } from '@angular/material/expansion';
import { PaymentPlatformService } from '../../../services';
import { PaymentMethodType } from '@aiwyn/api';
import { AbstractPaymentMethod, PaymentMethodMode } from '../../../models';
import { UntypedFormControl } from '@angular/forms';

@Component({
  selector: 'app-stripe-select-saved-payment-method',
  templateUrl: './stripe-select-saved-payment-method.component.html',
  styleUrls: ['./stripe-select-saved-payment-method.component.scss'],
  standalone: false,
})
export class StripeSelectSavedPaymentMethodComponent implements OnInit, OnDestroy {
  readonly PaymentMethodType = PaymentMethodType;
  readonly PaymentMethodMode = PaymentMethodMode;
  @ViewChild('savedPaymentMethodsAccordion') savedPaymentMethodsAccordion: MatAccordion;

  /**
   * Selected payment method ID.
   */
  selectedPaymentMethodId$ = new BehaviorSubject<string>(undefined);
  @Input() set selectedPaymentMethodId(selectedPaymentMethodId: string) {
    emitValueIfValid(selectedPaymentMethodId, this.selectedPaymentMethodId$);
  }

  /**
   * If intent is 'payment', this will be used to determine the payment flow.
   */
  paymentMethodMode$ = new BehaviorSubject<PaymentMethodMode>(undefined);
  @Input() set paymentMethodMode(paymentMethodMode: PaymentMethodMode) {
    emitValueIfValid(paymentMethodMode, this.paymentMethodMode$);
  }

  @Output() paymentMethodModeChange = new EventEmitter<PaymentMethodMode>();
  @Output() paymentMethodSelected = new EventEmitter<AbstractPaymentMethod>();
  @Output() updateBillingAddress = new EventEmitter<AbstractPaymentMethod>();

  private readonly _unsubscribe$ = new Subject<void>();

  paymentMethodModeIsSaved$ = this.paymentMethodMode$.pipe(
    distinctUntilChanged(),
    map(paymentMethodMode => paymentMethodMode === PaymentMethodMode.SAVED),
  );

  selectedSavedPaymentMethod$ = new BehaviorSubject<AbstractPaymentMethod>(undefined);
  emitSavedPaymentMethodSelection = this.selectedSavedPaymentMethod$.pipe(
    map(selectedSavedPaymentMethod => this.paymentMethodSelected.emit(selectedSavedPaymentMethod)),
    takeUntil(this._unsubscribe$),
  );

  setInitialSelectedPaymentMethodId$ = combineLatest([
    this.selectedPaymentMethodId$,
    this.paymentMethodMode$,
    this.paymentPlatformService.getSelectedPaymentMethodId(),
    this.paymentPlatformService.getDefaultPaymentMethodId(),
  ]).pipe(
    // TODO REFACTOR: May get rid of selectedIdFromState since payment method selection flows are decoupled now.
    map(([selectedIdFromParent, mode, selectedIdFromState, defaultId]) => {
      // cascade: selectedIdFromParent -> selectedIdFromState -> defaultId (selectedId may be a temporary payment method, so checking its mode first)
        const previousSelectedPaymentMethod = mode === PaymentMethodMode.SAVED
          ? selectedIdFromState
          : undefined;

      return selectedIdFromParent ?? previousSelectedPaymentMethod ?? defaultId;
      },
    ),
    take(1),
    switchMap(initialSavedPaymentMethodId => this.paymentPlatformService.getSavedPaymentMethod(initialSavedPaymentMethodId)),
    take(1),
    ignoreNullish(),
    tap(initialSavedPaymentMethod => {
      // TODO REFACTOR: Would there ever be a time we want to block setting the initial value?
      this.selectedSavedPaymentMethodControl.setValue(initialSavedPaymentMethod, { emitEvent: false });

      // avoid confusion by not emitting if simply selecting payment method specified by parent
      if (initialSavedPaymentMethod.id !== this.selectedPaymentMethodId$.value) {
        this.selectedSavedPaymentMethod$.next(initialSavedPaymentMethod);
      }
    }),
    takeUntil(this._unsubscribe$),
  );

  savedPaymentMethodAutocompleteOpen$ = new BehaviorSubject<boolean>(false);
  selectedSavedPaymentMethodControl = new UntypedFormControl(undefined);
  onSavedPaymentMethodSelected$ = getControlValueChanges<AbstractPaymentMethod>(this.selectedSavedPaymentMethodControl).pipe(
    ignoreNullish(),
    distinctUntilChanged((a, b) => a.id === b.id),
    tap(selectedSavedPaymentMethod => this.selectedSavedPaymentMethod$.next(selectedSavedPaymentMethod)),
    takeUntil(this._unsubscribe$),
  );

  selectSavedPaymentMethodOnExternalChange$ = this.selectedPaymentMethodId$.pipe(
    distinctUntilChanged(),
    filter(selectedPaymentMethodId =>
      this.selectedSavedPaymentMethodControl.value != undefined
      && selectedPaymentMethodId !== this.selectedSavedPaymentMethodControl.value.id,
    ),
    switchMap(selectedPaymentMethodId => this.paymentPlatformService.getSavedPaymentMethod(selectedPaymentMethodId).pipe(
      take(1),
      ignoreNullish(),
    )),
    tap(selectedSavedPaymentMethod => this.selectedSavedPaymentMethodControl.setValue(selectedSavedPaymentMethod, { emitEvent: false })),
    takeUntil(this._unsubscribe$),
  );

  savedPaymentMethods$ = this.paymentPlatformService.getSavedPaymentMethods().pipe(
    // TODO REFACTOR: Filter logic? V4 filters out ACHs that haven't been verified, but does V5 have that concept? Josh pl0x halp.
    // If billing address was incomplete and the user updated it, saved payment methods got refreshed. Update the selected payment method.
    switchMap(savedPaymentMethods => {
      if (!this.selectedSavedPaymentMethod$.value?.billingDetailsIncomplete) {
        return of(savedPaymentMethods);
      }

      return this.paymentPlatformService.getSavedPaymentMethod(this.selectedSavedPaymentMethod$.value.id).pipe(
        tap(selectedSavedPaymentMethod => {
          this.selectedSavedPaymentMethod$.next(selectedSavedPaymentMethod);
        }),
        mapTo(savedPaymentMethods),
      );
    }),
  );

  // cards first then banks (then sort by brand and last four)
  sortSavedPaymentMethodsFn = (a: AbstractPaymentMethod, b: AbstractPaymentMethod) => compareStringsIgnoreCase(a.sortKey, b.sortKey);
  compareSavedPaymentMethodsFn = (a: AbstractPaymentMethod, b: AbstractPaymentMethod) => a.id === b.id;
  disablePaymentMethodFn = (savedPaymentMethod: AbstractPaymentMethod) => !savedPaymentMethod.isValid;
  savedPaymentMethodDisplayFn = (savedPaymentMethod: AbstractPaymentMethod) => `
    <div class="d-flex align-items-center me-2">
      <img src="${savedPaymentMethod.icon}" width="16" height="16" alt="" class="me-2">
      ${savedPaymentMethod.name} •••• ${savedPaymentMethod.lastFour} ${savedPaymentMethod.isDefault ? '(default)' : ''}
      ${savedPaymentMethod.isValid ? '' : ' ' + savedPaymentMethod.invalidMessage}
    </div>
  `;

  panelWidth$ = new BehaviorSubject<number>(undefined);
  resizeObserver = new ResizeObserver(() => this.panelWidth$.next(this.host.nativeElement.clientWidth));

  dismissSavedPaymentMethodPanel$ = this.paymentMethodMode$.pipe(
    distinctUntilChanged(),
    pairwise(),
    filter(([previousMode]) => previousMode === PaymentMethodMode.SAVED),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly host: ElementRef<StripeSelectSavedPaymentMethodComponent & HTMLElement>,
  ) {
    this.resizeObserver.observe(this.host.nativeElement);
    this.onSavedPaymentMethodSelected$.subscribe();
    this.selectSavedPaymentMethodOnExternalChange$.subscribe();
    this.emitSavedPaymentMethodSelection.subscribe();
  }

  ngOnInit(): void {
    // emission will be lost if subscribed to in constructor
    this.setInitialSelectedPaymentMethodId$.subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
